import React, { useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Box, Paper, LinearProgress } from '@mui/material';
import { IconUpload, IconFile, IconX, IconAlertCircle } from '@tabler/icons-react';
import { uploadEmployeeDocumentAndReferenceInFirestore, uploadOrganizationDocumentAndReferenceInFirestore } from '../../../repository/DocumentsDAO';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import Logo from '../../../resources/images/logo/bizismart.png';
import DuplicateFileModal from './DuplicateFileModal';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const AddDocumentModal = ({ show, onHide, organization, type, employee }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [documentTitle, setDocumentTitle] = useState("");
    const [fileName, setFileName] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState("");
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    const validateFile = (file) => {
        setError("");
        
        if (!file.type.includes('pdf')) {
            setError(t(StringKeys.ONLY_PDF_FILES_ALLOWED));
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            setError(t(StringKeys.FILE_TOO_LARGE));
            return false;
        }

        return true;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && validateFile(file)) {
            setSelectedFile(file);
            setFileName(file.name);
            setDocumentTitle(file.name);
        }
    };

    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const file = event.dataTransfer.files[0];
        if (file && validateFile(file)) {
            setSelectedFile(file);
            setFileName(file.name);
            if (!documentTitle) {
                setDocumentTitle(file.name.replace('.pdf', ''));
            }
        }
    }, [documentTitle]);

    const handleUpload = async (duplicateAction) => {
        if (!selectedFile || !documentTitle.trim()) {
            setError(t(StringKeys.PLEASE_SELECT_A_FILE_AND_ENTER_A_TITLE));
            return;
        }

        setIsUploading(true);
        setError("");

        try {
            const uploadFunction = type === "EmployeeDocument" 
                ? uploadEmployeeDocumentAndReferenceInFirestore 
                : uploadOrganizationDocumentAndReferenceInFirestore;

            const result = await uploadFunction(
                organization,
                employee,
                selectedFile,
                documentTitle,
                duplicateAction
            );

            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_ADDED_X, { x: documentTitle }), ToastType.SUCCESS);
                onHide();
                resetModal();
            } else if (result.error === 'DUPLICATE_FILE') {
                setShowDuplicateModal(true);
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            setError(t(StringKeys.FAILED_TO_ADD_X, { x: documentTitle }));
            showToast(t(StringKeys.FAILED_TO_ADD_X, { x: documentTitle }), ToastType.DANGER);
        } finally {
            setIsUploading(false);
        }
    };

    const resetModal = () => {
        setSelectedFile(null);
        setDocumentTitle("");
        setFileName("");
        setError("");
        setIsUploading(false);
    };

    return (
        <>
            <Dialog 
                open={show} 
                onClose={() => {
                    resetModal();
                    onHide();
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ 
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 2
                }}>
                    <img src={Logo} alt="logo" style={{ height: '30px', marginRight:"20px" }} />
                    {t(StringKeys.ADD_DOCUMENT)}
                </DialogTitle>
                
                <DialogContent sx={{ mt: 2 }}>
                    <Box>
                        <Typography><IconAlertCircle/> Document will be made available to all employees in your organization within your organization's home page.</Typography>
                    </Box>


                    <Box
                        sx={{
                            mt: 3,
                            mb: 2,
                            position: 'relative',
                            height: '200px'
                        }}
                    >
                        <Box
                            onDrop={handleDrop}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDragging(true);
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDragging(false);
                            }}
                            component="label"
                            sx={{
                                height: '100%',
                                width: '100%',
                                border: '2px dashed',
                                borderColor: error ? 'error.main' : isDragging ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: 'primary.main'
                                }
                            }}
                        >
                            {!selectedFile ? (
                                <>
                                    <IconUpload size={48} color={isDragging ? "#1976d2" : "#666"} />
                                    <Typography variant="subtitle1" sx={{ mt: 2, color: 'text.secondary' }}>
                                        {isDragging ? t(StringKeys.DROP_HERE) : t(StringKeys.DRAG_AND_DROP_OR_CLICK_TO_UPLOAD)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                                        PDF only, max 10MB
                                    </Typography>
                                </>
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2
                                }}>
                                    <IconFile size={32} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" noWrap>
                                            {fileName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                        </Typography>
                                    </Box>
                                    {!isUploading && (
                                        <IconX
                                            size={20}
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelectedFile(null);
                                                setFileName("");
                                            }}
                                        />
                                    )}
                                </Box>
                            )}
                            <input
                                type="file"
                                hidden
                                onChange={handleFileChange}
                                accept="application/pdf"
                                disabled={isUploading}
                            />
                        </Box>
                    </Box>

                    {error && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            color: 'error.main',
                            mt: 1 
                        }}>
                            <IconAlertCircle size={16} />
                            <Typography variant="caption" color="error">
                                {error}
                            </Typography>
                        </Box>
                    )}

                    {isUploading && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress />
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                {t(StringKeys.UPLOADING_PLEASE_WAIT)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ 
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: 2
                }}>
                    <Button 
                        onClick={() => {
                            resetModal();
                            onHide();
                        }} 
                        disabled={isUploading}
                        sx={{ mr: 1 }}
                    >
                        {t(StringKeys.CANCEL)}
                    </Button>
                    <Button 
                        onClick={() => handleUpload('ask')} 
                        disabled={!selectedFile || !documentTitle.trim() || isUploading}
                        variant="contained"
                    >
                        {isUploading ? t(StringKeys.UPLOADING) : t(StringKeys.UPLOAD_FILE)}
                    </Button>
                </DialogActions>
            </Dialog>

            <DuplicateFileModal
                open={showDuplicateModal}
                onClose={() => setShowDuplicateModal(false)}
                onAction={(action) => {
                    setShowDuplicateModal(false);
                    handleUpload(action);
                }}
                fileName={documentTitle}
            />
        </>
    );
};

export default AddDocumentModal;